import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { confirmOrder } from "../../axios/api/OrderApi";
import { getDashboardData } from "../../axios/api/dashboardApi";
import {
  changePopUpStatus,
  setRequestStatus,
} from "../../redux/slices/popUpSlice";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaPhoneAlt } from "react-icons/fa";

const statusStyles = {
  new: { bg: "bg-primary text-white", label: "new" },
  inProgress: { bg: "bg-primary text-white", label: "inProgress" },
  finished: { bg: "bg-[#007100] text-white", label: "finished" },
  canceled: { bg: "bg-[#be0000] text-white", label: "canceled" },
};

const OrderModal = ({ onClose, rowData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleModalClick = (e) => e.stopPropagation();

  useEffect(() => {
    document.body.classList.add("modal-open");

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const handleClick = async (orderId) => {
    const responseData = await confirmOrder(orderId);
    if (responseData) {
      getDashboardData();
      dispatch(changePopUpStatus(true));
      dispatch(setRequestStatus(responseData));
      onClose();
    }
  };

  const currentStatus = statusStyles[rowData.status] || {
    bg: "bg-gray-200",
    label: rowData.status,
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);

    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
    const formattedTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return (
      <span>
        {formattedDate} | {formattedTime}
      </span>
    );
  };

  const formatAmount = (amount) => {
    const price = Number(amount) || 0;
    const formatted = price.toLocaleString("de-DE");
    return (
      <span>
        {formatted} {"\u058F"}
      </span>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-[20]"
      onClick={onClose}
    >
      <div
        className="block-background-design dark:!bg-secondary_black rounded-xl shadow-custom-deep relative mx-2 md:mx-0 p-5 w-full md:w-[700px]"
        onClick={handleModalClick}
      >
        {/* HEADLINE: Order # | Status */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-[600] text-[20px] md:text-[25px]">
            {t("orderNumber")} | {`${rowData.orderId}`}
          </h2>
          <span
            className={`font-[500] text-[16px] md:text-[20px] px-5 py-1 rounded-[5px] ${currentStatus.bg}`}
          >
            {t(currentStatus.label)}
          </span>
        </div>

        {/* ORDER DATE */}
        <div className="font-[500] text-[16px] mb-4">
          {t("orderDate")}: {formatDate(rowData.orderDate)}
        </div>

        {/* SERVICE BLOCK */}
        <div className="relative grid md:flex gap-4 mb-4 p-4 bg-[#e9e9e9] dark:bg-[#111111] rounded-[10px] dark:border-[#272727] border-gray-300 border-2 shadow-md items-start">
          <img
            src={
              `https://www.myservice.am/${rowData.image}` ||
              "/assets/cleaning.png"
            }
            alt="service"
            className="w-full md:w-[300px] h-[170px] object-cover rounded-[10px]"
          />
          <div className="flex-1 flex flex-col justify-start gap-1">
            <span className="font-[600] text-[20px] text-primary">
              {t(rowData.serviceName)}
            </span>
            <p className="font-[500] text-[15px] text-midnightBlue">
              {Array.isArray(rowData.orderDetails) &&
                rowData.orderDetails.map((item, index) => (
                  <span key={index}>
                    {t(item)}
                    <br />
                  </span>
                ))}
            </p>
            <span className="relative md:absolute bottom-0 md:bottom-[10px] font-[600] text-[30px]">
              {formatAmount(rowData.amount)}
            </span>
          </div>
        </div>

        {/* CANCELED / COMMENT */}
        {rowData.status === "canceled" && rowData.cancelReason && (
          <div className="flex mb-4 p-3 bg-[#e9e9e9] dark:bg-[#111111] rounded-[5px] dark:border-[#272727] border-gray-300 border-2 shadow-md">
            <span className="font-[500] text-[16px] mr-2">
              {t("cancelationReason")} |{" "}
            </span>
            <p className="font-[500] text-[16px]">{rowData.cancelReason}</p>
          </div>
        )}

        {rowData.status === "finished" && rowData.userComment && (
          <div className="flex mb-4 p-3 bg-[#e9e9e9] dark:bg-[#111111] rounded-[5px] dark:border-[#272727] border-gray-300 border-2 shadow-md">
            <span className="font-[500] text-[16px] mr-2">
              {t("comment")} |
            </span>
            <p className="font-[500] text-[16px]">{rowData.userComment}</p>
          </div>
        )}

        {/* ADDRESS & PHONE */}
        <div className="flex flex-col gap-2 mb-4">
          <span className="flex items-center font-[500] text-[16px]">
            <FaMapMarkerAlt className="text-primary mr-2 w-5 h-5" />{" "}
            {rowData.address}
          </span>
          <span className="flex items-center font-[500] text-[16px]">
            <FaPhoneAlt className="text-primary mr-2 w-5 h-5" />{" "}
            {rowData.phoneNumber}
          </span>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-3 mt-4">
          {rowData.status === "new" && (
            <>
              <button
                className="bg-primary hover:brightness-90 text-white prose-t16_bold py-3 px-6 rounded-xl"
                onClick={() => handleClick(rowData.orderId)}
              >
                {t("accept")} {t("order")}
              </button>
              <button
                className="border border-primary text-primary hover:bg-primary hover:text-white prose-t16_bold py-3 px-6 rounded-[7px]"
                onClick={onClose}
              >
                {t("close")}
              </button>
            </>
          )}
          {["inProgress", "finished", "canceled"].includes(rowData.status) && (
            <button
              className="border border-primary text-primary hover:bg-primary hover:text-white prose-t16_bold py-3 px-6 rounded-[5px]"
              onClick={onClose}
            >
              {t("close")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderModal;
